import { useState, useEffect } from 'react';
import { Search, X, Upload, CheckCircle, AlertCircle, Loader, MapPin, Phone, Building2, Lightbulb } from 'lucide-react';
import { useClaimBusiness } from '../hooks/useClaimBusiness.js';
import { storageService } from '../domain/services/StorageService.js';

const ClaimBusiness = () => {
  const [activeTab, setActiveTab] = useState('new');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [myClaims, setMyClaims] = useState([]);
  const [loadingClaims, setLoadingClaims] = useState(false);
  const [claimsFilter, setClaimsFilter] = useState('');

  const {
    searchQuery: hookSearchQuery,
    businesses,
    loading,
    error,
    userData,
    claimingId,
    formData,
    uploadProgress,
    uploadingDocs,
    setSearchQuery: setHookSearchQuery,
    searchBusinesses,
    submitClaim,
    updateFormField,
    updateUploadProgress,
    resetForm,
  } = useClaimBusiness();

  useEffect(() => {
    if (activeTab === 'my-claims') {
      fetchMyClaims();
    }
  }, [activeTab, claimsFilter]);

  const fetchMyClaims = async () => {
    setLoadingClaims(true);
    try {
      const token = storageService.getAuthToken();
      const params = new URLSearchParams({
        page: '1',
        take: '20',
      });
      if (claimsFilter) {
        params.append('status', claimsFilter);
      }

      const response = await fetch(
        `https://api.kaha.com.np/main/api/v3/businesses/claims-my?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error('Failed to fetch claims');
      const data = await response.json();
      setMyClaims(data.data || []);
    } catch (err) {
      console.log('Failed to fetch claims:', err);
    } finally {
      setLoadingClaims(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setHookSearchQuery(searchQuery);
    searchBusinesses(searchQuery);
  };

  const handleSelectBusiness = (business) => {
    setSelectedBusiness(business);
    resetForm();
  };

  const handleClaimBusiness = async () => {
    if (selectedBusiness) {
      await submitClaim(selectedBusiness.id);
      setSelectedBusiness(null);
      setSearchQuery('');
      setHookSearchQuery('');
      // This will trigger searchBusinesses with empty query, clearing results
      searchBusinesses('');
      // Redirect to My Claims tab
      setActiveTab('my-claims');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/50';
      case 'approved':
        return 'bg-green-500/20 text-green-300 border border-green-500/50';
      case 'rejected':
        return 'bg-red-500/20 text-red-300 border border-red-500/50';
      case 'cancelled':
        return 'bg-gray-500/20 text-gray-300 border border-gray-500/50';
      default:
        return 'bg-gray-500/20 text-gray-300 border border-gray-500/50';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-black p-6">
      <div className="max-w-7xl mx-auto">
        {/* Tabs and Search */}
        <div className="flex items-center justify-between gap-6 mb-8 border-b border-white/10 pb-4">
          <div className="flex gap-8">
            <button
              onClick={() => setActiveTab('new')}
              className={`pb-4 font-semibold text-lg transition-all relative group ${
                activeTab === 'new'
                  ? 'text-green-400'
                  : 'text-gray-400 hover:text-gray-300'
              }`}
            >
              New Claim
              {activeTab === 'new' && (
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-green-400 to-emerald-400 rounded-full"></div>
              )}
            </button>
            <button
              onClick={() => setActiveTab('my-claims')}
              className={`pb-4 font-semibold text-lg transition-all relative group ${
                activeTab === 'my-claims'
                  ? 'text-green-400'
                  : 'text-gray-400 hover:text-gray-300'
              }`}
            >
              My Claims
              {activeTab === 'my-claims' && (
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-green-400 to-emerald-400 rounded-full"></div>
              )}
            </button>
          </div>

          {/* Search Card - Only on New Claim Tab */}
          {activeTab === 'new' && (
            <form onSubmit={handleSearch} className="w-80">
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-xl blur opacity-0 group-hover:opacity-100 transition duration-300"></div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch(e)}
                  placeholder="Search business..."
                  className="relative w-full px-5 py-3 bg-white/5 border-2 border-green-500/30 hover:border-green-500/50 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-green-400 focus:ring-2 focus:ring-green-400/20 transition-all pr-12"
                />
                {searchQuery ? (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchQuery('');
                      setHookSearchQuery('');
                      setSelectedBusiness(null);
                      searchBusinesses('');
                    }}
                    className="absolute right-3 top-3.5 text-gray-400 hover:text-white transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={loading || !searchQuery.trim()}
                    className="absolute right-3 top-3.5 text-gray-400 hover:text-green-400 disabled:opacity-30 transition-colors"
                  >
                    {loading ? (
                      <Loader className="w-5 h-5 animate-spin" />
                    ) : (
                      <Search className="w-5 h-5" />
                    )}
                  </button>
                )}
              </div>
            </form>
          )}
        </div>

        {/* New Claim Tab */}
        {activeTab === 'new' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Claim Form (always visible) */}
            <div className="lg:col-span-2 order-2 lg:order-1">
              {selectedBusiness ? (
                <div className="bg-gradient-to-br from-white/10 via-white/5 to-white/0 rounded-2xl border border-white/20 backdrop-blur-xl p-8 space-y-8 shadow-2xl">
                  {/* Business Header */}
                  <div className="flex items-start justify-between pb-8 border-b border-white/10">
                    <div className="flex gap-4">
                      {selectedBusiness.image && (
                        <div className="relative">
                          <div className="absolute inset-0 bg-gradient-to-br from-green-500/30 to-emerald-500/30 rounded-xl blur-lg"></div>
                          <img
                            src={selectedBusiness.image}
                            alt={selectedBusiness.name}
                            className="relative w-24 h-24 rounded-xl object-cover ring-2 ring-green-500/50"
                          />
                        </div>
                      )}
                      <div>
                        <h2 className="text-3xl font-bold bg-gradient-to-r from-green-300 to-emerald-400 bg-clip-text text-transparent mb-1">
                          {selectedBusiness.name}
                        </h2>
                        <p className="text-gray-400 mb-3">@{selectedBusiness.tag}</p>
                        {selectedBusiness.address && (
                          <p className="text-sm text-gray-400 flex items-center gap-2 mb-2">
                            <MapPin className="w-4 h-4 text-green-400" />
                            {selectedBusiness.address}
                          </p>
                        )}
                        {selectedBusiness.phone && (
                          <p className="text-sm text-gray-400 flex items-center gap-2 min-w-0">
                            <Phone className="w-4 h-4 text-green-400 flex-shrink-0" />
                            <span className="truncate">{selectedBusiness.phone}</span>
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Info Message */}
                  <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-xl p-4 backdrop-blur-sm">
                    <p className="text-sm text-green-300 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      Provide required information to claim this business.
                    </p>
                  </div>

                  {/* Form Fields */}
                  <div className="space-y-6">
                    {/* Owner Phone */}
                    <div className="group">
                      <label className="block text-sm font-semibold text-white mb-2 group-hover:text-green-300 transition-colors">
                        Owner Phone Number
                      </label>
                      <input
                        type="tel"
                        value={formData.phoneNumber}
                        disabled
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-gray-400 cursor-not-allowed focus:outline-none"
                      />
                    </div>

                    {/* Owner Name */}
                    <div className="group">
                      <label className="block text-sm font-semibold text-white mb-2 group-hover:text-green-300 transition-colors">
                        Owner Name
                      </label>
                      <input
                        type="text"
                        value={userData?.fullName || `${userData?.firstName || ''} ${userData?.lastName || ''}`.trim()}
                        disabled
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-gray-400 cursor-not-allowed focus:outline-none"
                      />
                    </div>

                    {/* Email */}
                    <div className="group">
                      <label className="block text-sm font-semibold text-white mb-2 group-hover:text-green-300 transition-colors">
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={formData.emailAddress}
                        disabled
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-gray-400 cursor-not-allowed focus:outline-none"
                      />
                    </div>

                    {/* Ownership Proof Documents */}
                    <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                      <label className="block text-sm font-semibold text-white mb-5">
                        Ownership proof documents
                      </label>
                      <div className="grid grid-cols-2 gap-6">
                        <DocumentUploadBox
                          label="Business Registration Certificate"
                          fieldName="businessRegistrationCertificate"
                          formData={formData}
                          updateFormField={updateFormField}
                          uploadProgress={uploadProgress}
                          updateUploadProgress={updateUploadProgress}
                        />
                        <DocumentUploadBox
                          label="PAN/VAT Certificate"
                          fieldName="panVatCertificate"
                          formData={formData}
                          updateFormField={updateFormField}
                          uploadProgress={uploadProgress}
                          updateUploadProgress={updateUploadProgress}
                        />
                      </div>
                    </div>

                    {/* Claim Reason */}
                    <div className="group">
                      <label className="block text-sm font-semibold text-white mb-2 group-hover:text-green-300 transition-colors">
                        Claim Reason <span className="text-red-400">*</span>
                      </label>
                      <textarea
                        value={formData.claimReason}
                        onChange={(e) => updateFormField('claimReason', e.target.value)}
                        rows="4"
                        className="w-full px-4 py-3 bg-white/5 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/30 transition-all resize-none"
                      />
                      <p className="text-xs text-gray-400 mt-2">You can edit this message if needed</p>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-4 pt-8 border-t border-white/10">
                    <button
                      onClick={handleClaimBusiness}
                      disabled={
                        claimingId === selectedBusiness.id ||
                        uploadingDocs ||
                        !formData.claimReason?.trim() ||
                        !formData.businessRegistrationCertificate ||
                        !formData.panVatCertificate
                      }
                      className="flex-1 py-3 bg-gradient-to-r from-green-500 via-green-600 to-emerald-600 hover:from-green-400 hover:via-green-500 hover:to-emerald-500 text-white font-semibold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg hover:shadow-green-500/50 hover:shadow-xl"
                    >
                      {claimingId === selectedBusiness.id || uploadingDocs ? (
                        <>
                          <Loader className="w-5 h-5 animate-spin" />
                          {uploadingDocs ? 'Uploading...' : 'Submitting...'}
                        </>
                      ) : (
                        'Submit Claim'
                      )}
                    </button>
                    <button
                      onClick={() => setSelectedBusiness(null)}
                      className="flex-1 py-3 bg-gray-500/20 hover:bg-gray-500/30 text-gray-300 font-semibold rounded-lg transition-all border border-gray-500/30 hover:border-gray-500/50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="bg-gradient-to-br from-white/10 via-white/5 to-white/0 rounded-2xl border border-white/20 backdrop-blur-xl p-8 lg:p-12 shadow-2xl flex items-center justify-center min-h-[600px]">
                  <div className="text-center max-w-md space-y-8">
                    <div className="space-y-3">
                      <h2 className="text-2xl font-bold text-white">Select a Business</h2>
                      <p className="text-gray-400 text-sm leading-relaxed">
                        Search for your business on the Left panel and select it to start the claim process. You'll need to provide ownership documents to verify your business.
                      </p>
                    </div>
                    <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/20 rounded-xl p-6 space-y-4 text-left">
                      <p className="text-xs font-semibold text-blue-300 uppercase tracking-wide">Next Steps</p>
                      <div className="space-y-3">
                        <div className="flex gap-3">
                          <div className="w-8 h-8 rounded-full bg-blue-500/20 border border-blue-500/50 flex items-center justify-center flex-shrink-0 text-sm font-bold text-blue-400">1</div>
                          <div>
                            <p className="text-sm font-medium text-white">Search your business</p>
                            <p className="text-xs text-gray-400 mt-0.5">Use the search box to find your business by name or tag</p>
                          </div>
                        </div>
                        <div className="flex gap-3">
                          <div className="w-8 h-8 rounded-full bg-blue-500/20 border border-blue-500/50 flex items-center justify-center flex-shrink-0 text-sm font-bold text-blue-400">2</div>
                          <div>
                            <p className="text-sm font-medium text-white">Click to select</p>
                            <p className="text-xs text-gray-400 mt-0.5">Select your business from the search results</p>
                          </div>
                        </div>
                        <div className="flex gap-3">
                          <div className="w-8 h-8 rounded-full bg-blue-500/20 border border-blue-500/50 flex items-center justify-center flex-shrink-0 text-sm font-bold text-blue-400">3</div>
                          <div>
                            <p className="text-sm font-medium text-white">Fill the form</p>
                            <p className="text-xs text-gray-400 mt-0.5">Upload documents and provide claim details</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20 rounded-xl p-4 space-y-2">
                      <p className="text-xs font-semibold text-amber-300 flex items-center gap-2">
                        <Lightbulb className="w-4 h-4" />
                        Pro Tip
                      </p>
                      <p className="text-xs text-gray-300">Have your business registration and PAN/VAT certificates ready before starting</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Search Results */}
            <div className="lg:col-span-1 space-y-6 order-1 lg:order-2">
              <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
                {businesses.map((business) => (
                  <button
                    key={business.id}
                    onClick={() => handleSelectBusiness(business)}
                    className={`w-full group relative overflow-hidden rounded-xl transition-all duration-300 ${
                      selectedBusiness?.id === business.id
                        ? 'bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-2 border-green-500/50 shadow-lg shadow-green-500/20'
                        : 'bg-white/5 border-2 border-white/10 hover:border-green-500/30 hover:bg-white/10'
                    }`}
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-green-500/0 to-emerald-500/0 group-hover:from-green-500/10 group-hover:to-emerald-500/10 transition-all duration-300"></div>
                    <div className="relative p-4 flex gap-3">
                      {business.image && (
                        <img
                          src={business.image}
                          alt={business.name}
                          className="w-14 h-14 rounded-lg object-cover flex-shrink-0 ring-2 ring-green-500/20 group-hover:ring-green-500/50 transition-all"
                        />
                      )}
                      <div className="flex-1 min-w-0 text-left">
                        <h3 className="font-bold text-white truncate group-hover:text-green-300 transition-colors">{business.name}</h3>
                        <p className="text-sm text-gray-400">@{business.tag}</p>
                        {business.address && (
                          <p className="text-xs text-gray-500 mt-1 line-clamp-1 flex items-center gap-1">
                            <MapPin className="w-3 h-3 flex-shrink-0" />
                            {business.address}
                          </p>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* My Claims Tab */}
        {activeTab === 'my-claims' && (
          <div className="space-y-6">
            {/* Filter Buttons */}
            <div className="flex gap-3 flex-wrap">
              {['', 'pending', 'approved', 'rejected', 'cancelled'].map((status) => (
                <button
                  key={status}
                  onClick={() => setClaimsFilter(status)}
                  className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                    claimsFilter === status
                      ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-lg shadow-green-500/50'
                      : 'bg-white/10 text-gray-300 hover:bg-white/20 border border-white/10'
                  }`}
                >
                  {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'All'}
                </button>
              ))}
            </div>

            {/* Claims Grid */}
            {loadingClaims ? (
              <div className="flex justify-center py-12">
                <Loader className="w-8 h-8 animate-spin text-green-400" />
              </div>
            ) : myClaims.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {myClaims.map((claim) => (
                  <div
                    key={claim.id}
                    className="bg-gradient-to-br from-white/10 to-white/5 border border-white/20 rounded-xl p-6 hover:border-white/40 hover:shadow-lg hover:shadow-white/10 transition-all group"
                  >
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <p className="text-xs text-gray-400">
                          {new Date(claim.createdAt).toLocaleDateString()} -{' '}
                          {new Date(claim.createdAt).toLocaleTimeString()}
                        </p>
                      </div>
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold capitalize ${getStatusColor(
                          claim.status
                        )}`}
                      >
                        {claim.status}
                      </span>
                    </div>

                    {/* Business Info */}
                    <div className="flex gap-3 mb-4">
                      {claim.business?.image && (
                        <img
                          src={claim.business.image}
                          alt={claim.business.name}
                          className="w-16 h-16 rounded-lg object-cover flex-shrink-0 ring-2 ring-green-500/20 group-hover:ring-green-500/50 transition-all"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-green-400 truncate group-hover:text-green-300 transition-colors">{claim.business?.name}</h3>
                        <p className="text-sm text-gray-400">@{claim.business?.tag}</p>
                      </div>
                    </div>

                    {/* Claim Reason */}
                    {claim.claimReason && (
                      <div className="bg-white/5 rounded-lg p-3 border border-white/10 group-hover:border-white/20 transition-all">
                        <p className="text-xs text-gray-400 mb-1">Claim Reason:</p>
                        <p className="text-sm text-gray-300 line-clamp-3">{claim.claimReason}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-800/50 rounded-full flex items-center justify-center mx-auto mb-3">
                  <CheckCircle className="w-8 h-8 text-gray-600" />
                </div>
                <p className="text-gray-400 text-lg">No claims found</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Document Upload Box Component
const DocumentUploadBox = ({
  label,
  fieldName,
  formData,
  updateFormField,
  uploadProgress,
  updateUploadProgress,
}) => {
  const [preview, setPreview] = useState(null);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);

      updateUploadProgress(fieldName, 0);
      const interval = setInterval(() => {
        updateUploadProgress(fieldName, () => {
          const current = uploadProgress[fieldName] || 0;
          if (current >= 100) {
            clearInterval(interval);
            return current;
          }
          return current + 10;
        });
      }, 100);
      updateFormField(fieldName, file);
    }
  };

  const handleRemove = () => {
    updateFormField(fieldName, null);
    updateUploadProgress(fieldName, 0);
    setPreview(null);
  };

  const file = formData[fieldName];

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-400 font-medium">{label}</p>
      {file ? (
        <div className="relative overflow-hidden rounded-lg border-2 border-green-500/50 bg-gradient-to-br from-green-500/15 to-emerald-500/10 p-4 group hover:border-green-500/70 transition-all">
          <div className="flex items-center justify-between gap-3">
            {preview && (
              <div className="flex-shrink-0">
                <img
                  src={preview}
                  alt="Preview"
                  className="w-12 h-12 rounded-lg object-cover ring-2 ring-green-500/50"
                />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm text-white font-medium truncate">{file.name}</p>
              <p className="text-xs text-green-400">Uploaded</p>
            </div>
            <button
              type="button"
              onClick={handleRemove}
              className="flex-shrink-0 p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      ) : (
        <label className="relative block group cursor-pointer">
          <div className="absolute inset-0 bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-lg blur opacity-0 group-hover:opacity-100 transition duration-300"></div>
          <div className="relative border-2 border-dashed border-green-500/40 group-hover:border-green-500/60 rounded-lg p-6 text-center transition-all bg-white/[0.02] group-hover:bg-white/[0.05]">
            <div className="flex flex-col items-center gap-2">
              <div className="w-12 h-12 rounded-lg bg-green-500/20 flex items-center justify-center group-hover:bg-green-500/30 transition-all">
                <Upload className="w-6 h-6 text-green-400 group-hover:scale-110 transition-transform" />
              </div>
              <div>
                <p className="text-sm font-semibold text-white">Click to upload</p>
                <p className="text-xs text-gray-400 mt-0.5">PNG, JPG, PDF up to 3MB</p>
              </div>
            </div>
          </div>
          <input
            type="file"
            onChange={handleFileChange}
            accept="image/*,.pdf"
            className="hidden"
          />
        </label>
      )}
    </div>
  );
};

export default ClaimBusiness;
